import { Fragment, useMemo, useState, type ReactNode } from 'react';
import {
  Button,
  SegmentedControl,
  Select,
  Table,
  TableWrap,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '@/components/ui';
import { TextLink } from '@/components/ui/TextLink';
import { ApiError } from '@/lib/api';
import { cx } from '@/lib/cx';
import { useRememberedState } from '@/lib/filter-memory/use-filter-memory';
import type { FabFilters, FabOption, FabSchoolRow } from '@/lib/types';
import { BarList } from './fab-ui';
import type { FabFilterState, FabStage } from './fab-filter-state';
import {
  PiCardField,
  PiCardList,
  PiEmpty,
  PiError,
  PiExpandButton,
  PiLoading,
  PiSection,
  PiSortHeader,
} from './pi-kit';
import {
  formatCount,
  formatRate,
  stageRate,
  type FabOverviewData,
  type FabStageRow,
} from './use-fab-data';

/**
 * SEED FAB Schools tab: one row per client school, the three stages merged by
 * school (`data.merged`, built by use-fab-data).
 *
 * - Search (`state.search`, URL `q`) lives in the page toolbar and filters
 *   client-side by name or domain; it never refetches.
 * - Sort is client-side and remembered. Default Applications, descending. A
 *   missing rate sorts last in either direction; ties fall back to the name.
 * - Admit rate = admits / applications; Enrol rate = enrolled / admits (each
 *   stage against the one before it, like the funnel's conversion pills).
 * - Totals row: the server's per-stage totals when nothing is searched; the sum
 *   of the matching rows while a search narrows the list. It always covers
 *   every matching row, not just the rows shown before "Show more".
 * - Every row expands inline. The expansion shows that school's row from
 *   `data.schools[state.stage]`, broken down by the remembered "Break down by"
 *   choice: Region uses the response's columns BY POSITION within that one
 *   response (keys are positional upstream and are never kept), Source and
 *   First product use the split maps, labelled from `filters`.
 */

export interface FabSchoolsTabProps {
  data: FabOverviewData | null;
  loading: boolean;
  error: unknown | null;
  state: FabFilterState;
  onStateChange(next: FabFilterState): void;
  filters: FabFilters | null;
}

type SortColumn = 'school' | 'applications' | 'admits' | 'enrolled' | 'admitRate' | 'enrolRate';
type SortDir = 'asc' | 'desc';
interface SortState {
  column: SortColumn;
  dir: SortDir;
}

const SORT_COLUMNS: readonly SortColumn[] = [
  'school',
  'applications',
  'admits',
  'enrolled',
  'admitRate',
  'enrolRate',
];
const DEFAULT_SORT: SortState = { column: 'applications', dir: 'desc' };
const PAGE_SIZE = 50;

function parseSort(raw: unknown): SortState | undefined {
  if (typeof raw !== 'object' || raw === null) return undefined;
  const { column, dir } = raw as { column?: unknown; dir?: unknown };
  if (dir !== 'asc' && dir !== 'desc') return undefined;
  const match = SORT_COLUMNS.find((c) => c === column);
  return match === undefined ? undefined : { column: match, dir };
}

type Breakdown = 'region' | 'source' | 'product';
const BREAKDOWN_OPTIONS: readonly { value: Breakdown; label: string }[] = [
  { value: 'region', label: 'Region' },
  { value: 'source', label: 'Source' },
  { value: 'product', label: 'First product' },
];

/** The stored value used to include 'none'; it now falls back to Region. */
function parseBreakdown(raw: unknown): Breakdown | undefined {
  return BREAKDOWN_OPTIONS.find((o) => o.value === raw)?.value;
}

const STAGE_OPTIONS: readonly { value: FabStage; label: string }[] = [
  { value: 'applications', label: 'Applications' },
  { value: 'admits', label: 'Admits' },
  { value: 'enrolled', label: 'Enrolled' },
];

const STAGE_LABEL: Record<FabStage, string> = {
  applications: 'Applications',
  admits: 'Admits',
  enrolled: 'Enrolled',
};

const NUMERIC_COLUMNS: readonly { column: Exclude<SortColumn, 'school'>; label: string; width: string }[] = [
  { column: 'applications', label: 'Applications', width: 'min-w-28' },
  { column: 'admits', label: 'Admits', width: 'min-w-24' },
  { column: 'enrolled', label: 'Enrolled', width: 'min-w-24' },
  { column: 'admitRate', label: 'Admit rate', width: 'min-w-28' },
  { column: 'enrolRate', label: 'Enrol rate', width: 'min-w-28' },
];

interface ViewRow {
  row: FabStageRow;
  admitRate: number | null;
  enrolRate: number | null;
}

interface Totals {
  applications: number;
  admits: number;
  enrolled: number;
}

function errorMessage(error: unknown): string {
  return error instanceof ApiError ? error.message : 'Funnel Analytics & BI schools could not be loaded.';
}

function matchesSearch(row: FabStageRow, needle: string): boolean {
  if (needle === '') return true;
  return (
    row.displayName.toLowerCase().includes(needle) ||
    (row.domain !== null && row.domain.toLowerCase().includes(needle))
  );
}

function SchoolCell({ row }: { row: FabStageRow }) {
  const sub = [row.domain, row.country].filter((v): v is string => v !== null && v !== '');
  return (
    <div className="min-w-0">
      {row.company !== null ? (
        <TextLink to={`/companies/${row.company.id}`} className="block truncate font-medium">
          {row.displayName}
        </TextLink>
      ) : (
        <div className="truncate font-medium text-fg">{row.displayName}</div>
      )}
      {(sub.length > 0 || (row.company !== null && row.company.name !== row.displayName)) && (
        <div className="flex min-w-0 flex-wrap items-center gap-x-2 text-xs text-fg-muted">
          {sub.length > 0 && <span className="truncate">{sub.join(' · ')}</span>}
          {row.company !== null && row.company.name !== row.displayName && (
            <span className="truncate">{row.company.name}</span>
          )}
        </div>
      )}
    </div>
  );
}

function labelFor(options: readonly FabOption[] | undefined, value: string): string {
  return options?.find((o) => o.value === value)?.label ?? value;
}

function breakdownRows(
  breakdown: Breakdown,
  stageRow: FabSchoolRow | null,
  data: FabOverviewData,
  stage: FabStage,
  filters: FabFilters | null,
): { key: string; label: string; value: number }[] {
  if (stageRow === null) return [];
  let rows: { key: string; label: string; value: number }[] = [];
  if (breakdown === 'region') {
    rows = data.schools[stage].columns.map((column, index) => ({
      key: String(index),
      label: column.label,
      value: stageRow.values[column.key] ?? 0,
    }));
  } else if (breakdown === 'source') {
    rows = Object.entries(stageRow.sources ?? {}).map(([value, count]) => ({
      key: value,
      label: labelFor(filters?.sources, value),
      value: count,
    }));
  } else {
    rows = Object.entries(stageRow.products ?? {}).map(([value, count]) => ({
      key: value,
      label: labelFor(filters?.products, value),
      value: count,
    }));
  }
  return rows.filter((r) => r.value > 0).sort((a, b) => b.value - a.value);
}

function valueOf(view: ViewRow, column: Exclude<SortColumn, 'school'>): number | null {
  return column === 'admitRate' || column === 'enrolRate' ? view[column] : view.row[column];
}

function formatColumn(view: ViewRow, column: Exclude<SortColumn, 'school'>): string {
  const value = valueOf(view, column);
  return column === 'admitRate' || column === 'enrolRate' ? formatRate(value) : formatCount(value);
}

function totalOf(totals: Totals, column: Exclude<SortColumn, 'school'>): string {
  if (column === 'admitRate') return formatRate(stageRate(totals.admits, totals.applications));
  if (column === 'enrolRate') return formatRate(stageRate(totals.enrolled, totals.admits));
  return formatCount(totals[column]);
}

export function FabSchoolsTab({
  data,
  loading,
  error,
  state,
  onStateChange,
  filters,
}: FabSchoolsTabProps) {
  const [sort, setSort] = useRememberedState<SortState>(
    'fab-schools-tab-sort',
    DEFAULT_SORT,
    parseSort,
  );
  const [breakdown, setBreakdown] = useRememberedState<Breakdown>(
    'fab-schools-breakdown',
    'region',
    parseBreakdown,
  );
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(() => new Set());

  const stage = state.stage;
  const needle = state.search.trim().toLowerCase();

  // "Show more" limit, reset whenever the search changes.
  const [limitState, setLimitState] = useState<{ needle: string; limit: number }>({
    needle,
    limit: PAGE_SIZE,
  });
  const limit = limitState.needle === needle ? limitState.limit : PAGE_SIZE;

  const rows = useMemo<ViewRow[]>(() => {
    if (data === null) return [];
    const view = data.merged
      .filter((row) => matchesSearch(row, needle))
      .map((row) => ({
        row,
        admitRate: stageRate(row.admits, row.applications),
        enrolRate: stageRate(row.enrolled, row.admits),
      }));
    const factor = sort.dir === 'asc' ? 1 : -1;
    const byName = (a: ViewRow, b: ViewRow) =>
      a.row.displayName.localeCompare(b.row.displayName, undefined, { sensitivity: 'base' });
    return view.sort((a, b) => {
      if (sort.column === 'school') {
        const diff = byName(a, b);
        return diff !== 0 ? diff * factor : 0;
      }
      const left = valueOf(a, sort.column);
      const right = valueOf(b, sort.column);
      if (left === null && right === null) return byName(a, b);
      if (left === null) return 1;
      if (right === null) return -1;
      return left !== right ? (left - right) * factor : byName(a, b);
    });
  }, [data, needle, sort.column, sort.dir]);

  const totals = useMemo<Totals | null>(() => {
    if (data === null) return null;
    if (needle === '') {
      return {
        applications: data.schools.applications.totals.total,
        admits: data.schools.admits.totals.total,
        enrolled: data.schools.enrolled.totals.total,
      };
    }
    return rows.reduce(
      (acc, { row }) => ({
        applications: acc.applications + row.applications,
        admits: acc.admits + row.admits,
        enrolled: acc.enrolled + row.enrolled,
      }),
      { applications: 0, admits: 0, enrolled: 0 },
    );
  }, [data, needle, rows]);

  const shown = rows.slice(0, limit);
  const remaining = rows.length - shown.length;

  const toggleRow = (schoolId: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(schoolId)) next.delete(schoolId);
      else next.add(schoolId);
      return next;
    });
  };

  const onHeaderClick = (column: SortColumn) => {
    setSort(
      sort.column === column
        ? { column, dir: sort.dir === 'asc' ? 'desc' : 'asc' }
        : { column, dir: column === 'school' ? 'asc' : 'desc' },
    );
  };

  const ariaSortOf = (column: SortColumn): 'ascending' | 'descending' | undefined =>
    sort.column !== column ? undefined : sort.dir === 'asc' ? 'ascending' : 'descending';

  const sortHeader = (column: SortColumn, label: string, align: 'left' | 'right') => (
    <PiSortHeader
      label={label}
      active={sort.column === column}
      direction={sort.column === column ? sort.dir : column === 'school' ? 'asc' : 'desc'}
      onClick={() => onHeaderClick(column)}
      align={align}
    />
  );

  const expandButton = (row: FabStageRow) => (
    <PiExpandButton
      expanded={expanded.has(row.schoolId)}
      onClick={() => toggleRow(row.schoolId)}
      label={`${expanded.has(row.schoolId) ? 'Hide' : 'Show'} breakdown for ${row.displayName}`}
    />
  );

  const breakdownPanel = (row: FabStageRow) => {
    if (data === null) return null;
    const stageRow = row.byStage[stage];
    const breakdownLabel =
      BREAKDOWN_OPTIONS.find((o) => o.value === breakdown)?.label.toLowerCase() ?? '';
    return (
      <BarList
        title={`${STAGE_LABEL[stage]} by ${breakdownLabel}`}
        total={stageRow?.total ?? null}
        rows={breakdownRows(breakdown, stageRow, data, stage, filters)}
        emptyText={`No ${STAGE_LABEL[stage].toLowerCase()} for this school.`}
      />
    );
  };

  const actions = (
    <div className="flex flex-wrap items-center gap-2">
      <SegmentedControl
        label="Stage"
        value={stage}
        options={STAGE_OPTIONS}
        onChange={(next) => onStateChange({ ...state, stage: next })}
      />
      <Select
        aria-label="Break down by"
        value={breakdown}
        onChange={(event) => {
          const next = parseBreakdown(event.target.value);
          if (next !== undefined) setBreakdown(next);
        }}
        className="h-9 w-40 max-sm:h-11"
      >
        {BREAKDOWN_OPTIONS.map((option) => (
          <option key={option.value} value={option.value}>
            By {option.label.toLowerCase()}
          </option>
        ))}
      </Select>
    </div>
  );

  let body: ReactNode;
  if (error !== null && data === null) {
    body = (
      <div className="p-4">
        <PiError message={errorMessage(error)} />
      </div>
    );
  } else if (data === null) {
    body = <PiLoading variant="table" rows={8} />;
  } else if (rows.length === 0 || totals === null) {
    body = (
      <PiEmpty
        title="No client schools match these filters."
        description={needle !== '' ? 'Clear the search to see every client school.' : undefined}
        action={
          needle !== '' ? (
            <Button variant="secondary" onClick={() => onStateChange({ ...state, search: '' })}>
              Clear search
            </Button>
          ) : undefined
        }
      />
    );
  } else {
    const colCount = 1 + NUMERIC_COLUMNS.length;
    body = (
      <>
        {error !== null && (
          <div className="p-3">
            <PiError message={errorMessage(error)} />
          </div>
        )}

        {/* Desktop */}
        <div className="max-md:hidden">
          <TableWrap frozenHeader>
            <Table>
              <THead>
                <TR>
                  <TH frozen="left" aria-sort={ariaSortOf('school')} className="min-w-72">
                    <span className="pl-9">{sortHeader('school', 'School', 'left')}</span>
                  </TH>
                  {NUMERIC_COLUMNS.map(({ column, label, width }) => (
                    <TH key={column} numeric aria-sort={ariaSortOf(column)} className={width}>
                      {sortHeader(column, label, 'right')}
                    </TH>
                  ))}
                </TR>
              </THead>
              <TBody>
                {shown.map((view) => {
                  const { row } = view;
                  const open = expanded.has(row.schoolId);
                  return (
                    <Fragment key={row.schoolId}>
                      <TR>
                        <TD frozen="left" className="max-w-96">
                          <div className="flex min-w-0 items-center gap-1">
                            <div className="shrink-0">{expandButton(row)}</div>
                            <SchoolCell row={row} />
                          </div>
                        </TD>
                        {NUMERIC_COLUMNS.map(({ column }) => (
                          <TD
                            key={column}
                            numeric
                            className={cx(
                              'tabular-nums',
                              column === 'applications' && 'font-medium',
                              (column === 'admitRate' || column === 'enrolRate') && 'text-fg-muted',
                            )}
                          >
                            {formatColumn(view, column)}
                          </TD>
                        ))}
                      </TR>
                      {open && (
                        <TR>
                          <TD colSpan={colCount} className="bg-surface-sunken">
                            <div className="max-w-xl pl-9">{breakdownPanel(row)}</div>
                          </TD>
                        </TR>
                      )}
                    </Fragment>
                  );
                })}
                <TR className="border-t border-hairline-strong">
                  <TD frozen="left" className="font-medium">
                    <span className="pl-9">Total</span>
                  </TD>
                  {NUMERIC_COLUMNS.map(({ column }) => (
                    <TD key={column} numeric className="font-medium tabular-nums">
                      {totalOf(totals, column)}
                    </TD>
                  ))}
                </TR>
              </TBody>
            </Table>
          </TableWrap>
        </div>

        {/* Phone */}
        <div className="grid gap-2 p-3 md:hidden">
          <PiCardList
            items={shown}
            getKey={(view) => view.row.schoolId}
            render={(view) => (
              <div className="grid gap-2">
                <div className="flex items-start justify-between gap-2">
                  <SchoolCell row={view.row} />
                  <div className="shrink-0">{expandButton(view.row)}</div>
                </div>
                <div className="grid grid-cols-2 gap-x-3 gap-y-1">
                  {NUMERIC_COLUMNS.map(({ column, label }) => (
                    <PiCardField key={column} label={label} value={formatColumn(view, column)} />
                  ))}
                </div>
                {expanded.has(view.row.schoolId) && (
                  <div className="border-t border-hairline pt-3">{breakdownPanel(view.row)}</div>
                )}
              </div>
            )}
          />
          <div className="rounded-md border border-hairline-strong bg-surface-sunken p-3">
            <div className="font-medium">Total</div>
            <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1">
              {NUMERIC_COLUMNS.map(({ column, label }) => (
                <PiCardField key={column} label={label} value={totalOf(totals, column)} />
              ))}
            </div>
          </div>
        </div>

        {remaining > 0 && (
          <div className="flex items-center justify-between gap-3 border-t border-hairline px-3 py-2 text-sm text-fg-muted">
            <span className="tabular-nums">
              {formatCount(shown.length)} of {formatCount(rows.length)}
            </span>
            <Button
              variant="secondary"
              size="sm"
              className="max-sm:h-11"
              onClick={() => setLimitState({ needle, limit: limit + PAGE_SIZE })}
            >
              Show {formatCount(Math.min(PAGE_SIZE, remaining))} more
            </Button>
          </div>
        )}
      </>
    );
  }

  return (
    <div className={cx(loading && data !== null && 'opacity-70')} aria-busy={loading}>
      <PiSection title="Client schools" count={data === null ? null : rows.length} actions={actions} flush>
        {body}
      </PiSection>
    </div>
  );
}
